/**
 * API tokens, for giving another program read access to this one.
 *
 * The whole screen is built around a single awkward fact: the secret exists
 * for one render and then never again. So minting one does not quietly add a
 * row — it puts the secret in front of the operator, with a copy button, and
 * refuses to go away until they say they have it.
 */

import { useEffect, useState } from 'react';
import type { ApiToken, CreateApiTokenResponse } from '@proxmon/shared';
import { relativeTime } from '../format.ts';
import { useStore } from '../state/store.ts';

export function ApiTokens() {
  const tokens = useStore((s) => s.tokens);
  const loadTokens = useStore((s) => s.loadTokens);
  const createToken = useStore((s) => s.createToken);
  const revokeToken = useStore((s) => s.revokeToken);
  const notify = useStore((s) => s.notify);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [expires, setExpires] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<CreateApiTokenResponse | null>(null);

  useEffect(() => {
    void loadTokens().catch(() => {
      // Non-fatal: the list shows empty and the operator can retry by reopening.
    });
  }, [loadTokens]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Give it a name, so you can tell later what it was for.');
      return;
    }

    const days = expires.trim() === '' ? null : Number(expires);
    if (days !== null && (!Number.isInteger(days) || days <= 0)) {
      setError('Expiry is a whole number of days, or blank for never.');
      return;
    }

    setBusy(true);
    try {
      setIssued(await createToken({ name: name.trim(), expiresInDays: days }));
      setName('');
      setExpires('');
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the token.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (token: ApiToken) => {
    setBusy(true);
    try {
      await revokeToken(token.id);
      notify('info', `Token "${token.name}" revoked.`);
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Could not revoke the token.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="setting-row" style={{ display: 'block' }}>
      <div className="text" style={{ marginBottom: 8 }}>
        <strong>API tokens</strong>
        <span>
          Let another program read this cluster over HTTP. Tokens are read-only — nothing reachable
          with one changes a setting here or touches Proxmox. A token belongs to no account, so
          revoking it is the only way to take it out of use.
        </span>
      </div>

      {issued && <IssuedToken issued={issued} onDone={() => setIssued(null)} />}

      {tokens === null ? (
        <p className="empty">Loading…</p>
      ) : tokens.length === 0 ? (
        <p className="empty">No tokens yet.</p>
      ) : (
        <div style={{ marginBottom: 10 }}>
          {tokens.map((token) => (
            <TokenRow key={token.id} token={token} busy={busy} onRevoke={() => void revoke(token)} />
          ))}
        </div>
      )}

      {open ? (
        <form onSubmit={submit}>
          {error && <div className="form-error">{error}</div>}
          <div className="field">
            <label htmlFor="token-name">Name</label>
            <input
              id="token-name"
              value={name}
              placeholder="home-assistant"
              spellCheck={false}
              onChange={(event) => setName(event.target.value)}
            />
            <div className="hint">What is going to use it. Shown in the list above and in the log.</div>
          </div>

          <div className="field">
            <label htmlFor="token-expires">Expires in (days)</label>
            <input
              id="token-expires"
              value={expires}
              placeholder="never"
              inputMode="numeric"
              onChange={(event) => setExpires(event.target.value)}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn primary" disabled={busy}>
              Create token
            </button>
            <button type="button" className="btn ghost" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button type="button" className="btn" onClick={() => setOpen(true)}>
          New token…
        </button>
      )}
    </div>
  );
}

function TokenRow({ token, busy, onRevoke }: { token: ApiToken; busy: boolean; onRevoke: () => void }) {
  const expired = token.expiresAt !== null && token.expiresAt <= Date.now();

  return (
    <div className="token">
      <div className="token-head">
        <span className="name">{token.name}</span>
        <span className="pill idle">read-only</span>
        {expired && <span className="pill danger">expired</span>}
        <button
          type="button"
          className="btn ghost"
          style={{ marginLeft: 'auto', padding: '3px 9px', fontSize: 12 }}
          disabled={busy}
          onClick={onRevoke}
        >
          Revoke
        </button>
      </div>
      <div className="token-meta">
        <code>{token.prefix}…</code>
        <span>
          {token.lastUsedAt === null ? 'never used' : `last used ${relativeTime(token.lastUsedAt)}`}
          {token.expiresAt !== null && ` · expires ${new Date(token.expiresAt).toLocaleDateString()}`}
        </span>
      </div>
    </div>
  );
}

/**
 * The one-time reveal. No auto-dismiss and no way to get it back: closing this
 * is the operator saying they have copied it.
 */
function IssuedToken({ issued, onDone }: { issued: CreateApiTokenResponse; onDone: () => void }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(issued.secret);
      setCopied(true);
    } catch {
      // Clipboard access is refused outside a secure context; the operator can
      // still select the text by hand, which is why it is shown in full.
      setCopied(false);
    }
  };

  return (
    <div className="warn-box" style={{ marginBottom: 12 }}>
      <strong>Copy this now.</strong> It is not stored and cannot be shown again. If you lose it,
      revoke the token and make another.
      <div className="ip-editor" style={{ marginTop: 9 }}>
        <input readOnly value={issued.secret} onFocus={(event) => event.target.select()} aria-label="New token" />
        <button type="button" className="btn" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="source-note">
        Send it as <code>Authorization: Bearer …</code> against <code>/api/v1/…</code>. It can read
        this cluster and change nothing.
      </div>
      <div style={{ marginTop: 10 }}>
        <button type="button" className="btn" onClick={onDone}>
          I have copied it
        </button>
      </div>
    </div>
  );
}
